import React, { useCallback, useEffect, useRef, useState } from "react";
import { aiBotPropTypes } from "./types";
import { DIALOGFLOW_SCRIPT_URL, DEFAULT_CONFIG } from "./constants";
import {
  injectWelcomeMessage,
  applyStyles,
  watchForMessageContainer,
  observeShadowRoot,
  clearMessengerStorage,
  reinjectWelcome,
  injectResetButton,
  findSlottedTitlebar,
  captureDialogflowConversationName,
} from "./AIBotHelper";

/**
 * AIBot – Dialogflow Messenger chat widget with configurable welcome message.
 * @param {import("./types").AIBotProps} props
 */
const AIBot = (props) => {
  const {
    projectId = DEFAULT_CONFIG.PROJECT_ID,
    agentId = DEFAULT_CONFIG.AGENT_ID,
    languageCode = DEFAULT_CONFIG.LANGUAGE_CODE,
    maxQueryLength = DEFAULT_CONFIG.MAX_QUERY_LENGTH,
    chatTitle = DEFAULT_CONFIG.CHAT_TITLE,
    chatIcon = DEFAULT_CONFIG.CHAT_ICON,
    disclaimerMessage = DEFAULT_CONFIG.DISCLAIMER_MESSAGE,
    welcomeGreeting = DEFAULT_CONFIG.WELCOME_GREETING,
    isGated = false,
    oauthClientId = DEFAULT_CONFIG.OAUTH_CLIENT_ID,
    accessTokenName = DEFAULT_CONFIG.ACCESS_TOKEN,
  } = props;

  if (isGated) {
    return null;
  }

  const [resetKey, setResetKey] = useState(0);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [authData, setAuthData] = useState({ oauthClientId: "", accessToken: "" });
  const messengerRef = useRef(null);
  const bubbleRef = useRef(null);
  const observersRef = useRef([]);
  const stylesAppliedRef = useRef(false);
  const welcomeInjectedRef = useRef(false);
  const autoOpenedRef = useRef(false);
  const lastUserQueryRef = useRef("");
  const welcomeLoggedRef = useRef(false);
  const conversationNameRef = useRef(null);
  const turnPositionRef = useRef(0);

  const handleReset = useCallback(() => {
    const messenger = messengerRef.current;
    setHasUserInteracted(false);
    autoOpenedRef.current = false;
    welcomeLoggedRef.current = false;
    conversationNameRef.current = null;
    lastUserQueryRef.current = "";
    turnPositionRef.current = 0;
    try {
      if (typeof messenger?.startNewSession === "function") {
        messenger.startNewSession({ retainHistory: false });
        setTimeout(() => {
          reinjectWelcome(messenger, disclaimerMessage, welcomeGreeting, welcomeInjectedRef, observersRef);
        }, 150);
        return;
      }
    } catch (_) {}
    clearMessengerStorage();
    stylesAppliedRef.current = false;
    welcomeInjectedRef.current = false;
    setResetKey((k) => k + 1);
  }, [disclaimerMessage, welcomeGreeting]);

  useEffect(() => {
    const scriptSelector = "script[data-df-messenger]";
    if (document.querySelector(scriptSelector)) {
      return;
    }

    const script = document.createElement("script");
    script.src = DIALOGFLOW_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.setAttribute("data-df-messenger", "true");

    script.onerror = () => {
      console.error("Failed to load Dialogflow Messenger script");
    };

    document.body.appendChild(script);

    return () => {
      const existingScript = document.querySelector(scriptSelector);
      if (existingScript?.parentNode) {
        existingScript.parentNode.removeChild(existingScript);
      }
    };
  }, []);

  // Auto-open on desktop (>= 1025px) only on first visit (key not yet set by DF).
  useEffect(() => {
    if (autoOpenedRef.current) return;

    try {
      if (sessionStorage.getItem("df-messenger-chatBubbleExpansion") !== null) {
        autoOpenedRef.current = true;
        return;
      }
    } catch (_) {}

    const desktopMq = window.matchMedia("(min-width: 1025px)");
    if (!desktopMq.matches) return;

    let intervalId = null;
    let stopId = null;

    const doOpen = () => {
      if (autoOpenedRef.current) return;

      // Re-check at the moment of actual open in case orientation/resize happened
      if (!window.matchMedia("(min-width: 1025px)").matches) return;

      const messenger = messengerRef.current;
      if (!messenger) return;

      // df-messenger opens its chat window when the FAB button inside its
      // shadow root is clicked. That is the only reliable cross-version way.
      const bubble = messenger.querySelector("df-messenger-chat-bubble");
      const bubbleShadow = bubble?.shadowRoot;
      if (!bubbleShadow) return;

      // The clickable FAB is a <button> or element with role="button" inside the bubble shadow
      const fabBtn =
        bubbleShadow.querySelector("button") ||
        bubbleShadow.querySelector("[role='button']");

      if (!fabBtn) return;

      // Only click if the chat is not already open
      const isAlreadyOpen = messenger.hasAttribute("opened") ||
        messenger.getAttribute("aria-expanded") === "true" ||
        !!bubbleShadow.querySelector(".chat-wrapper.expanded, .chat-wrapper[aria-hidden='false']");

      if (isAlreadyOpen) {
        autoOpenedRef.current = true;
        if (intervalId) { clearInterval(intervalId); intervalId = null; }
        if (stopId) { clearTimeout(stopId); stopId = null; }
        return;
      }

      fabBtn.click();
      autoOpenedRef.current = true;
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
      if (stopId) { clearTimeout(stopId); stopId = null; }
    };

    // Poll until the bubble shadow DOM is ready and we can click the FAB
    intervalId = setInterval(doOpen, 300);
    stopId = setTimeout(() => {
      if (intervalId) { clearInterval(intervalId); intervalId = null; }
    }, 15000);

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (stopId) clearTimeout(stopId);
    };
  }, [resetKey]);

  // Track first real user message to enable the reset button
  useEffect(() => {
    const messenger = messengerRef.current;
    if (!messenger) return;

    const handleUserMessage = () => {
      setHasUserInteracted(true);
    };

    // df-request-sent fires when the user sends a message
    messenger.addEventListener("df-request-sent", handleUserMessage);

    return () => {
      messenger.removeEventListener("df-request-sent", handleUserMessage);
    };
  }, [resetKey]);

  // Session path (projects/.../agents/.../sessions/...) only appears in the outgoing
  // request URL, captured synchronously right when each request is actually sent - so
  // the welcome disclaimer/greeting is logged here too, with a guaranteed-fresh value
  // (df-request-sent fires before the network call and would see a stale/null ref).
  // turn_position is not present in Dialogflow's response, so we number turns ourselves.
  const postConversation = useCallback((userQuery, agentResponse, conversationName) => {
    turnPositionRef.current += 1;
    fetch("/api/agent/conversation", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ userQuery, agentResponse, conversationName, turnPosition: turnPositionRef.current }),
    }).catch(() => {});
  }, []);

  useEffect(() => {
    return captureDialogflowConversationName((conversationName) => {
      conversationNameRef.current = conversationName;

      if (!welcomeLoggedRef.current) {
        welcomeLoggedRef.current = true;
        const welcomeText = [disclaimerMessage, welcomeGreeting].filter(Boolean).join(" ").trim();
        if (welcomeText) {
          postConversation("", welcomeText, conversationName);
        }
      }
    });
  }, [disclaimerMessage, welcomeGreeting, postConversation]);

  // Log each user/agent exchange to BigQuery, the same way search queries are logged.
  useEffect(() => {
    const messenger = messengerRef.current;
    if (!messenger) return;

    const handleRequestSent = (event) => {
      const requestBody = event?.detail?.data?.requestBody;
      lastUserQueryRef.current =
        requestBody?.text ?? requestBody?.queryInput?.text?.text ?? "";
    };

    const handleResponseReceived = (event) => {
      const userQuery = lastUserQueryRef.current;
      lastUserQueryRef.current = "";

      const messages = event?.detail?.data?.messages || [];
      const agentResponse = messages
        .filter((message) => message?.type === "text")
        .map((message) => (Array.isArray(message.text) ? message.text.join(" ") : message.text || ""))
        .join(" ")
        .trim();

      if (!userQuery && !agentResponse) return;

      postConversation(userQuery, agentResponse, conversationNameRef.current);
    };

    messenger.addEventListener("df-request-sent", handleRequestSent);
    messenger.addEventListener("df-response-received", handleResponseReceived);

    return () => {
      messenger.removeEventListener("df-request-sent", handleRequestSent);
      messenger.removeEventListener("df-response-received", handleResponseReceived);
    };
  }, [resetKey, postConversation]);

  useEffect(() => {
    fetch("/api/global/GetAIBotToken", { credentials: "same-origin" })
      .then((res) => {
        if (res.ok) return res.json();
        return { oauthClientId: "", accessToken: "" };
      })
      .then((data) => {
        setAuthData({
          oauthClientId: data.oauthClientId || "",
          accessToken: data.accessToken || "",
        });
      })
      .catch(() => {
        setAuthData({ oauthClientId: "", accessToken: "" });
      });
  }, []);

  useEffect(() => {
    const messenger = messengerRef.current;
    const bubble = bubbleRef.current;

    if (messenger) {
      messenger.setAttribute("project-id", projectId);
      messenger.setAttribute("agent-id", agentId);
      messenger.setAttribute("language-code", languageCode);
      messenger.setAttribute("max-query-length", maxQueryLength);
      // Token comes from the API fetch (authData), NOT from props.
      // oauthClientId/accessTokenName props are kept for legacy fallback only.
      const resolvedOauthClientId = authData.oauthClientId || oauthClientId;
      const resolvedAccessToken   = authData.accessToken   || accessTokenName;
      if (resolvedAccessToken) {
        messenger.setAttribute("access-token-name", resolvedAccessToken);
      }
      if (resolvedOauthClientId) {
        messenger.setAttribute("oauth-client-id", resolvedOauthClientId);
      }
    }

    if (bubble) {
      bubble.setAttribute("chat-title", chatTitle);
      if (chatIcon) {
        bubble.setAttribute("chat-icon", chatIcon);
      }
    }
  }, [resetKey, projectId, agentId, languageCode, maxQueryLength, chatTitle, chatIcon, oauthClientId, accessTokenName, authData]);

  useEffect(() => {
    const messenger = messengerRef.current;
    if (!messenger || stylesAppliedRef.current) return;

    const initializeStyles = () => {
      const bubble = messenger.querySelector("df-messenger-chat-bubble");
      if (!bubble) return;

      if (stylesAppliedRef.current) return;

      if (applyStyles(bubble)) {
        stylesAppliedRef.current = true;
        return;
      }

      observeShadowRoot(bubble, (bubbleShadowRoot) => {
        if (stylesAppliedRef.current) return;

        const chatElement = bubbleShadowRoot.querySelector("df-messenger-chat");
        if (!chatElement) return;

        observeShadowRoot(chatElement, (chatShadowRoot) => {
          if (stylesAppliedRef.current) return;

          const messageListElement = chatShadowRoot.querySelector("df-messenger-message-list");
          if (!messageListElement) return;

          observeShadowRoot(messageListElement, () => {
            if (stylesAppliedRef.current) return;
            
            if (applyStyles(bubble)) {
              stylesAppliedRef.current = true;
            }
          }, observersRef);
        }, observersRef);
      }, observersRef);
    };

    initializeStyles();

    const handleChatOpen = () => {
      if (!stylesAppliedRef.current) {
        initializeStyles();
      }
    };

    messenger.addEventListener("df-chat-open-changed", handleChatOpen);
    messenger.addEventListener("opened", handleChatOpen);

    return () => {
      messenger.removeEventListener("df-chat-open-changed", handleChatOpen);
      messenger.removeEventListener("opened", handleChatOpen);
    };
  }, [resetKey]);

  useEffect(() => {
    const messenger = messengerRef.current;
    if (!messenger) return;

    welcomeInjectedRef.current = false;
    let retryIntervalId = null;
    // Track only observers created by THIS effect instance for safe cleanup
    const localObservers = [];
    const localObserversRef = { current: localObservers };

    const initializeWelcomeMessage = () => {
      if (welcomeInjectedRef.current) return true;

      const bubble = messenger.querySelector("df-messenger-chat-bubble");
      if (!bubble?.shadowRoot) return false;

      const chatElement = bubble.shadowRoot.querySelector("df-messenger-chat");
      if (!chatElement?.shadowRoot) return false;

      const messageListElement = chatElement.shadowRoot.querySelector("df-messenger-message-list");
      if (!messageListElement?.shadowRoot) return false;

      const messageListShadow = messageListElement.shadowRoot;

      const messageContainer =
        messageListShadow.querySelector("#message-list .content") ||
        messageListShadow.querySelector(".content") ||
        messageListShadow.querySelector("#message-list");

      if (messageContainer) {
        const success = injectWelcomeMessage(
          messageListShadow,
          disclaimerMessage,
          welcomeGreeting
        );
        if (success) {
          welcomeInjectedRef.current = true;
          if (retryIntervalId) {
            clearInterval(retryIntervalId);
            retryIntervalId = null;
          }
          return true;
        }
      }

      watchForMessageContainer(
        messageListShadow,
        welcomeInjectedRef,
        localObserversRef,
        disclaimerMessage,
        welcomeGreeting
      );
      return false;
    };

    initializeWelcomeMessage();

    if (!welcomeInjectedRef.current) {
      retryIntervalId = setInterval(() => {
        if (initializeWelcomeMessage()) {
          clearInterval(retryIntervalId);
          retryIntervalId = null;
        }
      }, 200);
    }

    const handleChatOpen = () => {
      setTimeout(() => {
        if (!welcomeInjectedRef.current) {
          initializeWelcomeMessage();
        }
      }, 50);

      setTimeout(() => {
        if (!welcomeInjectedRef.current) {
          initializeWelcomeMessage();
        }
      }, 300);
    };

    messenger.addEventListener("df-chat-open-changed", handleChatOpen);
    messenger.addEventListener("opened", handleChatOpen);
    messenger.addEventListener("df-messenger-loaded", handleChatOpen);

    return () => {
      messenger.removeEventListener("df-chat-open-changed", handleChatOpen);
      messenger.removeEventListener("opened", handleChatOpen);
      messenger.removeEventListener("df-messenger-loaded", handleChatOpen);
      if (retryIntervalId) clearInterval(retryIntervalId);
      // Only disconnect observers created by this effect instance
      localObservers.forEach((observer) => observer.disconnect());
    };
  }, [resetKey, disclaimerMessage, welcomeGreeting]);

  useEffect(() => {
    const messenger = messengerRef.current;
    if (!messenger) return;

    const tryInject = () => {
      injectResetButton(messenger, handleReset);
    };

    tryInject();
    const onOpen = () => setTimeout(tryInject, 100);
    messenger.addEventListener("df-chat-open-changed", onOpen);
    messenger.addEventListener("opened", onOpen);

    const id = setInterval(tryInject, 300);
    const t = setTimeout(() => clearInterval(id), 12000);

    return () => {
      messenger.removeEventListener("df-chat-open-changed", onOpen);
      messenger.removeEventListener("opened", onOpen);
      clearInterval(id);
      clearTimeout(t);
    };
  }, [resetKey, handleReset]);

  // Update reset button disabled state whenever hasUserInteracted changes
  useEffect(() => {
    const messenger = messengerRef.current;
    if (!messenger) return;

    const titlebar = findSlottedTitlebar(messenger);
    const btn = titlebar?.querySelector(".stewart-reset-btn");
    if (!btn) return;
    btn.disabled = !hasUserInteracted;
    btn.setAttribute("aria-disabled", String(!hasUserInteracted));
  }, [hasUserInteracted, resetKey]);

  return (
    <div key={resetKey}>
      <df-messenger ref={messengerRef}>
        <df-messenger-chat-bubble ref={bubbleRef} />
      </df-messenger>
    </div>
  );
};

AIBot.propTypes = aiBotPropTypes;

export default AIBot;

